import { useState, useRef, useEffect, useCallback, useMemo, type KeyboardEvent } from "react"
import { createPortal } from "react-dom"
import { ChevronLeft, ChevronRight, Calendar, X } from "lucide-react"

type GlitchDatePickerProps = {
  value: string
  onChange: (iso: string) => void
  placeholder?: string
  disabled?: boolean
  "aria-label"?: string
  className?: string
  required?: boolean
}

const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"]
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
]

function pad(n: number) {
  return String(n).padStart(2, "0")
}

function toISO(y: number, m: number, d: number) {
  return `${y}-${pad(m + 1)}-${pad(d)}`
}

function parseISO(s: string): { year: number; month: number; day: number } | null {
  const parts = s.split("-")
  if (parts.length !== 3) return null
  const y = parseInt(parts[0], 10)
  const m = parseInt(parts[1], 10) - 1
  const d = parseInt(parts[2], 10)
  if (isNaN(y) || isNaN(m) || isNaN(d)) return null
  return { year: y, month: m, day: d }
}

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate()
}

function getFirstDayOfWeek(year: number, month: number) {
  return new Date(year, month, 1).getDay()
}

export function GlitchDatePicker({
  value,
  onChange,
  placeholder = "Select date…",
  disabled = false,
  "aria-label": ariaLabel = "Date",
  className,
  required,
}: GlitchDatePickerProps) {
  const [open, setOpen] = useState(false)
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({})
  const containerRef = useRef<HTMLDivElement>(null)
  const calendarRef = useRef<HTMLDivElement>(null)

  const parsed = useMemo(() => parseISO(value), [value])
  const today = useMemo(() => {
    const d = new Date()
    return { year: d.getFullYear(), month: d.getMonth(), day: d.getDate() }
  }, [])

  const [viewYear, setViewYear] = useState(parsed?.year ?? today.year)
  const [viewMonth, setViewMonth] = useState(parsed?.month ?? today.month)

  useEffect(() => {
    if (open && parsed) {
      setViewYear(parsed.year)
      setViewMonth(parsed.month)
    } else if (open) {
      setViewYear(today.year)
      setViewMonth(today.month)
    }
  }, [open, parsed, today])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handle = (e: MouseEvent) => {
      const t = e.target as Node
      if (
        containerRef.current && t && !containerRef.current.contains(t) &&
        calendarRef.current && !calendarRef.current.contains(t)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener("click", handle, true)
    return () => document.removeEventListener("click", handle, true)
  }, [open])

  // Position
  useEffect(() => {
    if (!open || !containerRef.current) return
    const update = () => {
      if (!containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const openAbove = spaceBelow < 340 && rect.top > spaceBelow
      setDropdownStyle({
        position: "fixed",
        left: Math.max(8, Math.min(rect.left, window.innerWidth - 296)),
        width: 288,
        ...(openAbove
          ? { bottom: window.innerHeight - rect.top + 4, top: "auto" }
          : { top: rect.bottom + 4, bottom: "auto" }),
        zIndex: 10001,
      })
    }
    update()
    window.addEventListener("scroll", update, true)
    window.addEventListener("resize", update)
    return () => {
      window.removeEventListener("scroll", update, true)
      window.removeEventListener("resize", update)
    }
  }, [open])

  const prevMonth = useCallback(() => {
    setViewMonth((m) => {
      if (m === 0) { setViewYear((y) => y - 1); return 11 }
      return m - 1
    })
  }, [])

  const nextMonth = useCallback(() => {
    setViewMonth((m) => {
      if (m === 11) { setViewYear((y) => y + 1); return 0 }
      return m + 1
    })
  }, [])

  const selectDay = useCallback((day: number) => {
    onChange(toISO(viewYear, viewMonth, day))
  }, [onChange, viewYear, viewMonth])

  const goToToday = useCallback(() => {
    onChange(toISO(today.year, today.month, today.day))
    setViewYear(today.year)
    setViewMonth(today.month)
  }, [onChange, today])

  const relativeLabel = useMemo(() => {
    if (!parsed) return "Today"
    const sel = new Date(parsed.year, parsed.month, parsed.day)
    const now = new Date(today.year, today.month, today.day)
    const diff = Math.round((sel.getTime() - now.getTime()) / 86400000)
    if (diff === 0) return "Today"
    if (diff === 1) return "tomorrow"
    if (diff === -1) return "yesterday"
    if (diff >= 7 && diff <= 13) return "next week"
    if (diff <= -7 && diff >= -13) return "last week"
    if (diff >= 28 && diff <= 34) return "next month"
    if (diff <= -28 && diff >= -34) return "last month"
    if (diff > 0) return `in ${diff} days`
    return `${Math.abs(diff)} days ago`
  }, [parsed, today])

  const daysInMonth = getDaysInMonth(viewYear, viewMonth)
  const firstDay = getFirstDayOfWeek(viewYear, viewMonth)

  const displayValue = parsed
    ? `${MONTHS[parsed.month].slice(0, 3)} ${parsed.day}, ${parsed.year}`
    : placeholder

  const handleKeyDown = (e: KeyboardEvent) => {
    if (disabled) return
    if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
      e.preventDefault()
      setOpen(true)
    }
  }

  return (
    <div ref={containerRef} className={`relative ${className ?? ""}`}>
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={ariaLabel}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-xl border border-white/[0.08] bg-white/[0.04] text-white/80 text-left font-mono text-[11px] focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400/30 disabled:opacity-50 transition-colors hover:border-white/[0.12]"
      >
        <Calendar className="w-3.5 h-3.5 shrink-0 text-white/30" aria-hidden />
        <span className={`truncate flex-1 ${!parsed ? "text-white/40" : ""}`}>
          {displayValue}
        </span>
      </button>

      {/* Hidden native input for form required validation */}
      {required && (
        <input
          type="text"
          required
          value={value}
          tabIndex={-1}
          className="absolute inset-0 opacity-0 pointer-events-none"
          onChange={() => {}}
          aria-hidden
        />
      )}

      {open && createPortal(
        <div
          ref={calendarRef}
          role="dialog"
          aria-label="Date picker"
          style={dropdownStyle}
          className="rounded-xl border border-white/[0.08] bg-black/90 backdrop-blur-2xl shadow-2xl p-3 select-none"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Month/year header */}
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={prevMonth}
              className="p-1 rounded-lg hover:bg-white/[0.06] text-white/50 hover:text-white/80 transition-colors"
              aria-label="Previous month"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="font-mono text-[11px] font-bold text-white/80 uppercase tracking-widest">
              {MONTHS[viewMonth].slice(0, 3)} {viewYear}
            </span>
            <div className="flex items-center gap-0.5">
              <button
                type="button"
                onClick={nextMonth}
                className="p-1 rounded-lg hover:bg-white/[0.06] text-white/50 hover:text-white/80 transition-colors"
                aria-label="Next month"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1 rounded-lg hover:bg-white/[0.06] text-white/30 hover:text-white/60 transition-colors"
                aria-label="Close date picker"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 gap-0 mb-1">
            {DAYS.map((d) => (
              <div
                key={d}
                className="text-center font-mono text-[9px] font-bold uppercase tracking-widest text-white/25 py-1"
              >
                {d}
              </div>
            ))}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 gap-0">
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`empty-${i}`} className="h-8" />
            ))}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1
              const isSelected = parsed?.year === viewYear && parsed?.month === viewMonth && parsed?.day === day
              const isToday = today.year === viewYear && today.month === viewMonth && today.day === day
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => selectDay(day)}
                  className={`h-8 w-full rounded-lg font-mono text-[11px] transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-teal-400/40 ${
                    isSelected
                      ? "bg-teal-500/20 text-teal-300 font-bold border border-teal-400/30"
                      : isToday
                        ? "text-red-400 font-bold hover:bg-white/[0.06]"
                        : "text-white/60 hover:bg-white/[0.06] hover:text-white/80"
                  }`}
                >
                  {day}
                </button>
              )
            })}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/[0.06]">
            <button
              type="button"
              onClick={goToToday}
              title="Today"
              className="group relative font-mono text-[10px] text-teal-400/70 hover:text-teal-400 transition-colors uppercase tracking-widest"
            >
              <span className="group-hover:opacity-0 transition-opacity">{relativeLabel}</span>
              <span className="absolute left-0 top-0 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">Reset to today</span>
            </button>
            {parsed && (
              <span className="font-mono text-[10px] text-white/30">
                {MONTHS[parsed.month]} {parsed.day}, {parsed.year}
              </span>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
